import React, { useContext } from "react";
import { useRef, useState } from "react";

import { useMutation, useQuery } from "@tanstack/react-query";
import { Button, Col, Divider, Form, Grid, Input, Popconfirm, Row, Select, Skeleton, notification } from "antd";
import { useForm, useWatch } from "antd/es/form/Form";

import AdicionarAjuda, { ITutorial, ITutorialPassos } from "../../../Components/AdicionarAjuda";
import { AgenciaService } from "../../../Services/Financeiro/AgenciaService";
import { BancoService } from "../../../Services/Financeiro/BancoService";
import { PropriedadesDoPopConfirm } from "../../../Defaults";
import { PropriedadesDeNotificacao } from "../../../Utils/Notificacoes";
import { SelectComPesquisa } from "../../../Components/SelectComPesquisa";
import { TypeAgencia } from "../../../Models/Financeiro/Agencia";

import { Permissoes } from "../../../Constants/Permissoes";
import { SessaoContext } from "../../../Contexts/SessaoContext";
import { Restrito } from "../../../Components/Restrito";

import { ResponsiveAntdTable } from "../../../Components/ResponsiveAntdTable";
import { Conjunto } from "../../../Components/Conjunto";
import { Ordenar } from "../../../Utils/Ordenacao";

import { Link, matchPath, Route, Routes, useNavigate } from "react-router-dom";
import { useRelativePath } from "../../../Hooks/useNextRelativePath";
import { useRedirection } from "../../../Hooks/useRedirection";
import { useWatchParameterOnRoutes } from "../../../Hooks/useGetParameterOnRoutes";

import * as Tutoriais from "../Agencia/agencia.tutorial";

const agenciaService = new AgenciaService();
const bancoService = new BancoService();

const ListagemDeAgencias: React.FC<{
    aoEditar?: (item: TypeAgencia) => any,
    aoAdicionar?: () => any

    hrefDoBotaoAdicionar?: string,
    hrefDoBotaoEditar?: (agencia: TypeAgencia) => string | void
}> = (props) => {

    const permissoes = useContext(SessaoContext)?.sessao?.usuario?.permissoes.map((p) => p.nome);
    const tamanhoDaTela = Grid.useBreakpoint();

    const [filtro, setFiltro] = useState<TypeAgencia>({});

    const listaAgencia = useQuery({
        queryKey: ['listarAgencias', filtro],
        queryFn: () => agenciaService.listar(filtro),
        gcTime: 0,
        enabled: true
    });

    interface IFormFiltro {
        txtNomeAgencia?: string,
        txtNumeroAgencia?: string,
        ddlAtivo?: boolean
    }

    const filtrarTabela = (values: IFormFiltro) => {
        const agenciaFiltro: TypeAgencia = {
            nomeAgencia: values.txtNomeAgencia,
            numeroAgencia: values.txtNumeroAgencia,
            ativo: values.ddlAtivo 
        }
        setFiltro(agenciaFiltro);
    }

    const adicionar = permissoes?.includes(Permissoes.CriarAgencia);

    return (
        <>
            <Form name="listar" onFinish={filtrarTabela} layout='vertical' autoComplete="off">
                <Row gutter={16} id={Tutoriais.IDs.formularioDeFiltro} >
                    <Col xs={24} md={6} id={Tutoriais.IDs.filtroDeNomeAgencia}>
                        <Form.Item<IFormFiltro> name="txtNomeAgencia" label='Nome da agência'>
                            <Input placeholder="Todos"/>
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={7} id={Tutoriais.IDs.filtroDeNumeroAgencia}>
                        <Form.Item<IFormFiltro> name="txtNumeroAgencia" label='Número da agência'>
                            <Input placeholder="Todos"/>
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={3} id={Tutoriais.IDs.filtroDeAtivo}>
                        <Form.Item<IFormFiltro> name="ddlAtivo" label='Ativo' initialValue={true}>
                            <Select
                                allowClear
                                placeholder={"Todos"}
                                options={[
                                    { value: true, label: "Ativo" },
                                    { value: false, label: "Inativo" }
                                ]} />
                        </Form.Item>
                    </Col>
                    <Col xs={adicionar ? 12 : 24} md={adicionar ? 3 : 5} style={{ alignItems: 'end', display: 'flex' }}>
                        <Form.Item style={{ flexGrow: 1 }}>
                            <Button type="primary" id={Tutoriais.IDs.botaoListar} key="submit" htmlType='submit' style={{ width: "100%" }}
                                disabled={listaAgencia.isFetching}
                            >
                                Listar
                            </Button>
                        </Form.Item>
                    </Col>
                    
                    <Restrito aberto={adicionar}>
                        <Col xs={12} md={5} style={{ alignItems: 'end', display: 'flex' }}>
                            <Form.Item style={{ flexGrow: 1 }}>
                                <Link to={props.hrefDoBotaoAdicionar ?? "#"} onClick={(e) => { !props.hrefDoBotaoAdicionar && e.preventDefault() }}>
                                    <Button type="primary" onClick={() => { props.aoAdicionar?.() }} style={{ width: "100%" }}>
                                        Adicionar
                                    </Button>
                                </Link>
                            </Form.Item>
                        </Col>
                    </Restrito>
                </Row>
                <Row>
                    <Col xs={24}>
                        <ResponsiveAntdTable<TypeAgencia>
                            bordered
                            locale={{ emptyText: 'Nenhum agencia encontrado.' }}
                            dataSource={listaAgencia.data?.data}
                            loading={listaAgencia.isFetching}
                            rowKey={(k) => k.codigo!}
                            addTitleOn="lg"
                            columns={[
                                {
                                    title: "Agência",
                                    breakpoints: { xs: 24, sm: 24, lg: 6 },
                                    dataIndex: ["nomeAgencia"],
                                    sorter: (a, b) => Ordenar.String(a.nomeAgencia, b.nomeAgencia),
                                    key: "nomeAgencia",
                                },
                                {
                                    title: "Banco",
                                    breakpoints: { xs: 24, sm: 24, lg: 6 },
                                    dataIndex: ["banco", "nome"],
                                    sorter: (a, b) => Ordenar.String(a.banco?.nome, b.banco?.nome),
                                    key: "banco-nome",
                                },
                                {
                                    title: "Número",
                                    breakpoints: { xs: 24, sm: 24, lg: 3 },
                                    dataIndex: ["numeroAgencia"],
                                    sorter: (a, b) => Ordenar.Numero(a.numeroAgencia != undefined ? parseInt(a.numeroAgencia) : undefined, b.numeroAgencia != undefined ? parseInt(b.numeroAgencia) : undefined),
                                    key: "numeroAgencia",
                                },
                                {
                                    title: "Dígito",
                                    breakpoints: { xs: 24, sm: 24, lg: 3 },
                                    dataIndex: ["digitoAgencia"],
                                    sorter: (a, b) => Ordenar.Numero(a.digitoAgencia != undefined ? parseInt(a.digitoAgencia) : undefined, b.digitoAgencia != undefined ? parseInt(b.digitoAgencia) : undefined),
                                    key: "digitoAgencia",
                                },
                                {
                                    title: "Ativo",
                                    dataIndex: ["ativo"],
                                    breakpoints: { xs: 12, sm: 12, lg: 2 },
                                    key: "ativo",
                                    sorter: (a, b) => Ordenar.Booleano(a.ativo, b.ativo),
                                    render: (v: boolean) => {
                                        return <>{v ? "Sim" : "Não"}</>
                                    }
                                },
                                {
                                    title: "Ações",
                                    key: "acoes",
                                    noResponsiveTitle: true,
                                    breakpoints: { xs: 24, sm: 24, lg: 4 },
                                    hidden: [Permissoes.AtualizarAgencia, Permissoes.DesativarAgencia, Permissoes.AtivarAgencia].every((p) => !permissoes?.includes(p)),
                                    render: (_, item) => {
                                        return <Conjunto
                                            style={{ justifyContent: tamanhoDaTela.sm ? "unset" : "center", display: "flex", flexWrap: 'wrap' }}
                                            itens={
                                                (() => {
                                                    if (item.ativo) {
                                                        return [
                                                            permissoes?.includes(Permissoes.CriarAgencia) &&
                                                            <Link to={props.hrefDoBotaoEditar?.(item) ?? "#"}
                                                                onClick={(e) => !props.hrefDoBotaoEditar && e.preventDefault()}
                                                            >
                                                                <Button
                                                                    className={Tutoriais.Classes.botaoEditar}
                                                                    type='primary'
                                                                    size='small'
                                                                    onClick={() => {
                                                                        props.aoEditar?.(item);
                                                                    }}
                                                                >
                                                                    Editar
                                                                </Button>
                                                            </Link>,

                                                            permissoes?.includes(Permissoes.DesativarAgencia) &&
                                                            <Popconfirm
                                                                {...PropriedadesDoPopConfirm("desativar")}
                                                                onConfirm={() => {
                                                                    if (item.codigo == undefined) return;
                                                                    agenciaService.desativar(item.codigo)
                                                                        .then(() => {
                                                                            listaAgencia.refetch()
                                                                        })
                                                                        .catch(() => { })
                                                                }}
                                                            >
                                                                <Button type='default' danger size='small' className={Tutoriais.Classes.botaoDesativar}>
                                                                    Desativar
                                                                </Button>
                                                            </Popconfirm>
                                                        ]
                                                    } else {
                                                        return [
                                                            permissoes?.includes(Permissoes.AtivarAgencia) &&
                                                            <Popconfirm
                                                                {...PropriedadesDoPopConfirm("ativar")}
                                                                onConfirm={() => {
                                                                    if (item.codigo == undefined) return;
                                                                    agenciaService.ativar(item.codigo)
                                                                        .then(() => {
                                                                            listaAgencia.refetch()
                                                                        })
                                                                        .catch(() => { })
                                                                }}
                                                            >
                                                                <Button type='dashed' size='small'>Reativar</Button>
                                                            </Popconfirm>
                                                        ]
                                                    }

                                                })()
                                            }
                                        />
                                    }
                                },
                            ]}

                            csv={{
                                enabled: true,
                                filename: "agencias.csv"
                            }}
                        />
                    </Col>
                </Row>

            </Form>

            <AdicionarAjuda tutoriais={[
                {
                    titulo: "Como consultar agências",
                    tutorial: Tutoriais.ConsultarAgencia
                },

                permissoes?.includes(Permissoes.CriarAgencia) &&
                {
                    titulo: "Como cadastrar uma nova agência",
                    tutorial: Tutoriais.AdicionarAgencia
                },

                permissoes?.includes(Permissoes.AtualizarAgencia) &&
                {
                    titulo: "Como editar uma agência",
                    tutorial: Tutoriais.EditarAgencia
                },

                permissoes?.includes(Permissoes.DesativarAgencia) &&
                {
                    titulo: "Como desativar uma agência",
                    tutorial: Tutoriais.DesativarAgencia
                }
            ].filter((v) => v != false) as ITutorial[]} />
        </>
    )
}

const CadastroDeAgencia: React.FC<{
    agenciaEmAlteracao?: TypeAgencia,
    aoCancelar?: () => any,
    depoisDeFinalizar?: () => any
}> = (props) => {

    const usuario = useContext(SessaoContext)?.sessao?.usuario;

    const insereAgencia = useMutation({
        mutationFn: (agencia: TypeAgencia) => agenciaService.inserir(agencia),
        onSuccess: () => {
            notification.open({
                ...PropriedadesDeNotificacao.Sucesso('Cadastro realizado com sucesso.')
            })
            props.depoisDeFinalizar && props.depoisDeFinalizar()
        },
    });

    const atualizaAgencia = useMutation({
        mutationFn: (agencia: TypeAgencia) => agenciaService.atualizar(agencia),
        onSuccess: () => { 
            notification.open({
                ...PropriedadesDeNotificacao.Sucesso('Atualização realizada com sucesso.')
            })
            props.depoisDeFinalizar && props.depoisDeFinalizar()
        },
    });

    function onFinishAgencia(values: TypeAgencia) {
        const agenciaInserir: TypeAgencia = {
            ...values,
            codigo: props.agenciaEmAlteracao ? props.agenciaEmAlteracao.codigo : undefined
        }

        if (agenciaInserir?.codigo === undefined)
            insereAgencia.mutate(agenciaInserir!);
        else
            atualizaAgencia.mutate(agenciaInserir!);
    }

    const [formCadastro] = useForm();
    const dadosForm = useWatch([], formCadastro);

    const listaBanco = useQuery({
        queryKey: ['listarBancos'],
        queryFn: () => bancoService.listar({ ativo: true }),
        gcTime: 0,
        enabled: true
    });

    const txtCodAgencia = useRef(null)
    const txtNome = useRef(null)
    const ddlAtivo = useRef(null);
    const btnSalvar = useRef(null);

    const tutorialAdicionarAgencia: ITutorialPassos = {
        steps: [
            {
                title: "Passo 1",
                description: "Informe um código interno.",
                target: () => txtCodAgencia.current,
            },
            {
                title: "Passo 2",
                description: "Dê um nome ao agencia.",
                target: () => txtNome.current,
            },
            {
                title: "Passo 3",
                description: `Clique em "Salvar".`,
                target: () => btnSalvar.current,
            }
        ]
    }
    const tutorialMenuItems: ITutorial[] = [
        {
            titulo: "Como adicionar uma nova agência",
            tutorial: tutorialAdicionarAgencia
        },
    ]

    return (
        <>
            <Form
                autoComplete="off"
                onFinish={onFinishAgencia}
                form={formCadastro}
                layout="vertical"
            >
                <Row gutter={16}>
                    <Col xs={24} md={6} ref={ddlAtivo}>
                        <Form.Item<TypeAgencia> 
                            name="codBanco" 
                            label='Banco' 
                            initialValue={props.agenciaEmAlteracao?.codBanco}
                            rules={[
                                { 
                                    required: true, 
                                    message: 'Por favor, selecione o banco!' ,
                                }
                            ]} 
                        >
                            <SelectComPesquisa
                                allowClear
                                placeholder={"Selecione o banco"}
                                options={
                                    listaBanco.data?.data.map(m => {
                                        return {
                                            value: m.codigo,
                                            label: m.nome!
                                        }
                                    })
                                } />
                        </Form.Item>
                    </Col>

                    <Col xs={15} md={4} ref={txtCodAgencia}>
                        <Form.Item<TypeAgencia> 
                            name="numeroAgencia" 
                            rules={[
                                { 
                                    required: true, 
                                    validator: async (_, val: string) => {
                                        if (val == null || val.trim() == "")
                                            return Promise.reject("Por favor, informe o número da agência!")

                                        if (!val.match(/^[0-9]*$/))
                                            return Promise.reject("Valor inválido.")
                                    }
                                }
                            ]} 
                            initialValue={props.agenciaEmAlteracao?.numeroAgencia} 
                            label='Número'
                        >
                            <Input />
                        </Form.Item>
                    </Col>

                    <Col xs={9} md={2} ref={txtNome}>
                        <Form.Item<TypeAgencia> 
                            name="digitoAgencia" 
                            rules={[
                                { 
                                    validator: async (_, val: string) => {
                                        if (val == undefined)
                                            return

                                        if (!val.match(/^[0-9]*$/))
                                            return Promise.reject("Valor inválido.")
                                    }
                                }
                            ]} 
                            initialValue={props.agenciaEmAlteracao?.digitoAgencia} 
                            label='Dígito'
                        >
                            <Input />
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={6} ref={txtNome}>
                        <Form.Item<TypeAgencia>
                            name="nomeAgencia"
                            rules={[
                                { 
                                    required: true,
                                    validator: async (_, val: string) => {
                                        if (val == null || val.trim() == "")
                                            return Promise.reject("Por favor, informe o nome da agência!")
                                    }
                                }
                            ]}
                            initialValue={props.agenciaEmAlteracao?.nomeAgencia}
                            label='Nome'>
                            <Input />
                        </Form.Item>
                    </Col>
                    {
                        props.agenciaEmAlteracao &&
                        <Col xs={24} md={6} ref={ddlAtivo}>
                            <Form.Item name="ativo" label='Ativo' initialValue={props.agenciaEmAlteracao.ativo ? true : false}>
                                <Select
                                    allowClear
                                    placeholder={"Todos"}
                                    options={[
                                        { value: true, label: "Ativo" },
                                        { value: false, label: "Inativo" }
                                    ]} />
                            </Form.Item>
                        </Col>
                    }

                    <Col xs={24} md={6} style={{ alignItems: 'end', display: 'flex' }}>
                        <Form.Item>
                            {props.aoCancelar &&
                                <Button danger type="primary" onClick={() => {
                                    props.aoCancelar && props.aoCancelar()
                                }}>
                                    Cancelar
                                </Button>
                            }
                            <Button type="primary" ref={btnSalvar} key="submit" htmlType='submit' style={{ margin: '0px 8px' }}
                                disabled={!usuario?.permissoes.map((p) => p.nome).includes(props.agenciaEmAlteracao != undefined ? Permissoes.AtualizarAgencia : Permissoes.CriarAgencia)}
                            >
                                Salvar
                            </Button>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>

            <AdicionarAjuda tutoriais={tutorialMenuItems}/>
        </>
    )
}

export function Agencia() {
    const [agenciaEmAlteracao, setAgenciaEmAlteracao] = useState<TypeAgencia | undefined>(undefined);
    const permissoes = useContext(SessaoContext)?.sessao?.usuario?.permissoes.map((p) => p.nome);

    const navigate = useNavigate();
    const [raiz, subrota] = useRelativePath();

    const rotaDeEdicao = matchPath({ path: "/editar" }, subrota);
    const rotaDeVisualizacao = matchPath({ path: "/detalhes" }, subrota);

    const obterAgencia = useMutation({
        mutationFn: (id: number) => new AgenciaService().listar({ codigo: id, ativo: true }),
        onSuccess: (resposta) => {
            const resultados = resposta.data;
            if (resultados.length == 0) {
                notification.open({
                    ...PropriedadesDeNotificacao.Erro("Nenhum registro foi encontrado para o identificador informado")
                })
                navigate(raiz)
            } else {
                setAgenciaEmAlteracao(resposta.data.at(0))
            }
        }
    })

    useRedirection([Permissoes.ListarAgencias, Permissoes.CriarAgencia, Permissoes.AtualizarAgencia, Permissoes.DesativarAgencia])
    useWatchParameterOnRoutes({ 
        appliablePaths: [rotaDeEdicao, rotaDeVisualizacao], paramToWatch: "id", filters: [(p) => isNaN(Number(p))], 
        onNotAccepted() { navigate(raiz) },
        onExists(param) { obterAgencia.mutate(Number(param)); }, 
     })

    return ( 
        <>
            <h2 style={{ marginTop: 0 }}> 
                {
                    rotaDeVisualizacao ? "Detalhes do Agencia" : rotaDeEdicao? "Edição de Agência": "Cadastro de Agência"
                }
            </h2>

            <Divider />

            <Routes>
                {
                    permissoes?.includes(Permissoes.ListarAgencias) &&
                    <Route path="*" index element={
                        <ListagemDeAgencias
                            hrefDoBotaoAdicionar={raiz + "/cadastro"}
                            hrefDoBotaoEditar={(item) => {
                                if (item.codigo) {
                                    return raiz + `/editar?id=${item.codigo}`
                                }
                            }}

                            aoEditar={(agencia) => {
                                setAgenciaEmAlteracao(agencia);
                            }}
                        />
                    } />
                }

                {
                    permissoes?.includes(Permissoes.CriarAgencia) &&
                    <Route path="/cadastro" element={
                        <CadastroDeAgencia
                            aoCancelar={() => { navigate(raiz) }}
                            depoisDeFinalizar={() => { navigate(raiz) }}
                        />
                    } />
                }

                {
                    permissoes?.includes(Permissoes.AtualizarAgencia) &&
                    <Route path="/editar" element={
                        agenciaEmAlteracao == undefined ? <Skeleton active /> :
                            <CadastroDeAgencia
                                agenciaEmAlteracao={agenciaEmAlteracao}
                                aoCancelar={() => { navigate(raiz) }}
                                depoisDeFinalizar={() => { navigate(raiz) }}
                            />
                    } />
                }
            </Routes>
        </>
    );
}